import React from 'react';
import classNames from 'classnames';
import i18next from 'i18next';

import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Link from 'components/Link';
import TitleLogo from 'components/TitleLogo';

import {
  package as PackagePropType,
  packageContext as packageContextTypes,
} from 'lib/CustomPropTypes';

import ALaCarteItem from './ALaCarteItem';

function renderTitle(title, titleUrl, logoUrl) {
  if (!title && !logoUrl) return null;

  const titleLogo = <TitleLogo logoUrl={logoUrl} title={title} />;
  return (
    <h2 className="a-la-carte__title" data-test="a-la-carte__title">
      {titleUrl ? (
        <Link href={titleUrl}>
          {titleLogo}
        </Link>
      ) : titleLogo}
    </h2>
  );
}

function renderDescription(description) {
  if (!description) return null;

  return (
    <p
      className="a-la-carte__description publico-txt f3 lh-copy fw4"
      data-test="a-la-carte__description"
    >
      {description}
    </p>
  );
}

function ALaCarte(props, context) {
  const { content } = props;
  const { railContext } = context;
  const { id, items = [], metadata: packageMetadata } = content;

  // In case of API error, not a valid case
  if (!items || !items.length) return null;

  const {
    title,
    titleUrl,
    description,
    logoUrl,
    seeAllUrl,
  } = packageMetadata || {};

  const isInRail = railContext !== null;

  // Use this flag below to swap between shrink sizing and static sizing
  // This can be utilized when Tease List is migrated to this component
  const isLatestStories = false;

  const seeAllText = packageMetadata.seeAllText || i18next.t('See All');

  const hasHeader = title || description;

  return (
    <div
      className={classNames('pkg a-la-carte', {
        // Sizing strategy is global modifier as it determines flexbox behavior and height
        'a-la-carte--shrink-to-fit': isLatestStories,

        // Modify styling based on placement in rail
        'a-la-carte--in-rail': isInRail,
      })}
      data-test="a-la-carte"
      data-packageid={id}
    >
      <div className="a-la-carte__wrapper">
        {
          hasHeader && (
          <div className="a-la-carte__top-decoration" data-test="a-la-carte__top-decoration" />
          )
        }
        { renderTitle(title, titleUrl, logoUrl) }
        { renderDescription(description) }
        <div
          className="a-la-carte__content"
        >
          <div className="a-la-carte__items-wrapper">
            <ul
              className="a-la-carte__items ph5 ph0-m"
              data-test="a-la-carte__items"
            >
              {
                items
                  .filter((item) => item) // filter out any null content
                  .map((item) => ({ // map package metadata with content metadata
                    ...item,
                    metadata: {
                      ...packageMetadata,
                      ...item.metadata,
                    },
                  }))
                  .map((item) => <ALaCarteItem key={item.id} item={item} />)
              }
            </ul>
          </div>
          { seeAllUrl && ( // only show button if URL is set
            <div
              className="a-la-carte__button-wrapper w-100 df"
              data-test="a-la-carte__button-wrapper"
            >
              <ButtonHoverAnimation
                size={isInRail ? 'normal' : 'small'}
                type="link"
                url={seeAllUrl}
                additionalClasses="a-la-carte__button"
                title={seeAllText}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ALaCarte.propTypes = {
  content: PackagePropType.isRequired,
};

ALaCarte.contextTypes = {
  ...packageContextTypes,
};

export default ALaCarte;
